import { useMutation } from '@tanstack/react-query';
import { update } from '@shared/typedApi';
import { Form } from '@components/shared/forms';
import { Notification } from '@components/shared';
import { useFormikContext } from 'formik';
import { throttle } from 'lodash';
import { useTranslation } from 'react-i18next';
import routes from '@root/routes';
import { stringify } from 'qs';
import { AdminMicrosite } from '@root/types';
import cn from 'classnames';
import FormFields from './FormFields';
import { micrositeFormValidationSchema } from './utils';

type Props = {
  buildingId: string;
  microsite: AdminMicrosite;
  refetch: () => void;
};

export default function EditMicrosite({ microsite, buildingId, refetch }: Props) {
  const { t } = useTranslation('admin');
  const mutation = useMutation(
    (form: Partial<AdminMicrosite>) => {
      const { topNavLogo, footerLogo, ...rest } = form;
      return update(routes.api.admin.buildingMicrosite(buildingId), {
        ...rest,
        topNavLogoAttributes: topNavLogo,
        footerLogoAttributes: footerLogo,
      });
    },
    {
      onSuccess: () => {
        Notification.success({
          title: t('common:changesSaved'),
          placement: 'topRight',
        });
        refetch();
      },
      onError: (error: Error) => {
        Notification.error({
          title: t('common:somethingWentWrong'),
          text: JSON.parse(error.message).errors[0].message,
          placement: 'topRight',
        });
      },
    },
  );

  return (
    <Form
      id="edit-microsite"
      layout="admin"
      initialValues={{
        font: microsite.font,
        sectionHeadingColor: microsite.sectionHeadingColor,
        backgroundColor: microsite.backgroundColor,
        headerBackgroundColor: microsite.headerBackgroundColor,
        textColor: microsite.textColor,
        headerTextColor: microsite.headerTextColor,
        headerBorderColor: microsite.headerBorderColor,
        homeBuildingInfoButtonColor: microsite.homeBuildingInfoButtonColor,
        homeBuildingInfoButtonTextColor: microsite.homeBuildingInfoButtonTextColor,
        homeBuildingInfoBackgroundColor: microsite.homeBuildingInfoBackgroundColor,
        homeBuildingInfoFontColor: microsite.homeBuildingInfoFontColor,
        homeBuildingInfoBorderColor: microsite.homeBuildingInfoBorderColor,
        homeAddressTextPlacement: microsite.homeAddressTextPlacement,
        homeHeroImageStyle: microsite.homeHeroImageStyle,
        footerBackgroundColor: microsite.footerBackgroundColor,
        footerTextColor: microsite.footerTextColor,
        footerBorderColor: microsite.footerBorderColor,
        availabilitiesStyle: microsite.availabilitiesStyle,
        availabilitiesBorderColor: microsite.availabilitiesBorderColor,
        availabilitiesButtonColor: microsite.availabilitiesButtonColor,
        availabilitiesButtonTextColor: microsite.availabilitiesButtonTextColor,
        status: microsite.status,
        linkedinUrl: microsite.linkedinUrl || '',
        twitterUrl: microsite.twitterUrl || '',
        facebookUrl: microsite.facebookUrl || '',
        instagramUrl: microsite.instagramUrl || '',
        tiktokUrl: microsite.tiktokUrl || '',
        footerLeftText: microsite.footerLeftText || '',
        footerRightText: microsite.footerRightText || '',
        contactsBackgroundColor: microsite.contactsBackgroundColor,
        contactsCardBorderColor: microsite.contactsCardBorderColor,
        featuresAmenitiesBackgroundColor: microsite.featuresAmenitiesBackgroundColor,
        featuresAmenitiesTextColor: microsite.featuresAmenitiesTextColor,
        featuresCertificationBorderColor: microsite.featuresCertificationBorderColor,
        tenantPortalUrl: microsite.tenantPortalUrl || '',
        topNavLogo: microsite.topNavLogo,
        footerLogo: microsite.footerLogo,
        footerLogoLink: microsite.footerLogoLink || '',
      }}
      onSubmit={(values, { resetForm }) => {
        mutation.mutate(values, {
          onSuccess: () => {
            resetForm({ values });
          },
        });
      }}
      validationSchema={micrositeFormValidationSchema(t)}
      className="relative h-[calc(100vh_-_320px)] w-full"
    >
      <div className="absolute bottom-0 left-0 right-0 top-0 grid grid-cols-[424px_720px]">
        <div className="flex flex-col gap-3 overflow-scroll pr-3">
          <FormFields microsite={microsite} />
        </div>
        <div className="overflow-hidden">
          <IFramePreview srcPath={microsite.previewUrl} />
        </div>
      </div>
    </Form>
  );
}

function IFramePreview({ srcPath }) {
  const { values, dirty } = useFormikContext<{ font: string }>();
  const getSrc = throttle(() => `${srcPath}?${stringify({ ...values })}`, 2000);
  const extraSpaceForSaveButtonBar = dirty && 'pb-3';

  return (
    <iframe
      data-testid="microsite-preview-iframe"
      title="microsite-preview"
      src={getSrc()}
      className={cn('h-[200%] w-[1440px] origin-top-left scale-50', extraSpaceForSaveButtonBar)}
    />
  );
}
